import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faBan,
  faUnlock,
  faTrash,
  faUser,
} from '@fortawesome/free-solid-svg-icons'
import Spinner from '@/components/Spinner'
import { BaseUser, UserType, UserAction } from './types'

export interface DetailSection {
  key: string
  label: string
  rows: Array<{
    key: string
    label: string
    value?: any
    render?: (user: any) => React.ReactNode
  }>
}

export interface UserDetailsPanelProps {
  user: BaseUser | null
  loading?: boolean
  error?: string | null
  sections: DetailSection[]
  userType?: UserType
  onAction?: (action: UserAction) => void
  onBack?: () => void
  showActions?: boolean
  isSuspended?: boolean
}

/**
 * Reusable UserDetailsPanel for displaying user details
 * Used by OrganizerDetailsPage and SingerDetailsPage
 */
export default function UserDetailsPanel({
  user,
  loading = false,
  error = null,
  sections,
  userType = 'organizer',
  onAction,
  onBack,
  showActions = true,
  isSuspended = false,
}: UserDetailsPanelProps) {
  const { t } = useTranslation()

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner />
      </div>
    )
  }

  if (error || !user) {
    return (
      <div className="space-y-6">
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            {t('common.backHome')}
          </button>
        )}
        <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
          <p className="text-red-300 font-mono text-sm">
            <span className="text-red-500">{'[ERROR]'}</span> {error || t('common.error')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header with Back Button */}
      {onBack && (
        <div>
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition mb-4"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            {t('common.backHome')}
          </button>
          <h1 className="text-4xl font-black bg-linear-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
            {user.profile?.firstName} {user.profile?.lastName}
          </h1>
          <p className="text-slate-400 mt-2 font-mono text-sm">
            {'> '} <span className="text-purple-400">{userType}</span>{' --details'}
          </p>
        </div>
      )}

      {/* Detail Sections */}
      {sections.map((section) => (
        <div
          key={section.key}
          className="bg-slate-900 border border-slate-800 rounded-xl p-8"
        >
          <h2 className="text-xl font-bold text-white mb-6">{section.label}</h2>
          <div className="grid grid-cols-2 gap-6">
            {section.rows.map((row) => {
              const value = row.render ? row.render(user) : row.value ?? user[row.key as keyof BaseUser]
              
              // Check if this is the events list row (empty label and render function)
              const isEventsList = !row.label && row.render && row.key === 'eventsList'

              return (
                <div key={row.key} className={isEventsList ? 'col-span-2' : ''}>
                  {!isEventsList && (
                    <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase">
                      {row.label}
                    </label>
                  )}
                  <div className={isEventsList ? '' : 'text-slate-300 font-mono'}>
                    {value || '—'}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {/* Actions */}
      {showActions && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
          <h2 className="text-xl font-bold text-white mb-6">{t('common.actions')}</h2>
          {user?.isGuest && (
            <div className="mb-4 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
              <p className="text-slate-400 text-sm font-mono">
                <span className="text-cyan-400">[INFO]</span> Chanteur invité (pas de compte) - Actions limitées
              </p>
            </div>
          )}
          <div className="flex gap-4 flex-wrap">
            {!isSuspended && !user?.isGuest && (
              <button
                onClick={() => onAction?.('suspend')}
                className="flex items-center gap-2 px-4 py-2.5 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-lg transition"
              >
                <FontAwesomeIcon icon={faBan} />
                {t('superadmin.suspend')}
              </button>
            )}
            {isSuspended && !user?.isGuest && (
              <button
                onClick={() => onAction?.('unsuspend')}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-500 text-white font-bold rounded-lg transition"
              >
                <FontAwesomeIcon icon={faUnlock} />
                {t('common.restore')}
              </button>
            )}
            {!user?.isGuest && (
              <button
                onClick={() => onAction?.('reset-password')}
                className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-lg transition"
              >
                <FontAwesomeIcon icon={faUser} />
                Réinitialiser mot de passe
              </button>
            )}
            <button
              onClick={() => onAction?.('delete')}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-500 text-white font-bold rounded-lg transition"
            >
              <FontAwesomeIcon icon={faTrash} />
              {t('common.delete')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

